/* eslint-disable no-else-return */
import { Dies } from 'components/utility-component/wafer-map-widget/wafer-map-v2/Utils/WaferMapVariablesClassV2';
import {
  ColAxisDirection, RowAxisDirection, WaferAxisDirection, NotchPosition, DefaultNotchPosition,
} from 'components/utility-component/wafer-map-widget/wafer-map/web-gl-utils/Enums';
import {
  DieType, RectCoord, ReticleReference, Viewport, WaferMapData, XYPoint,
} from 'components/utility-component/wafer-map-widget/wafer-map/web-gl-utils/Types';
// eslint-disable-next-line no-unused-vars
import { DataGrid } from 'devextreme-react';
import { NOTCH_FLAT_TOGGLE_DIAMETER_VALUE } from './wafer-control-map';

const IMPERIAL_TO_METRIC_CONVERSION_MAPPINGS = [
  {
    imperialUnit: 'INCHES',
    metricUnit: 'MILLIMETERS',
    imperialVal: 3,
    metricVal: 76,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'MILLIMETERS',
    imperialVal: 4,
    metricVal: 100,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'MILLIMETERS',
    imperialVal: 6,
    metricVal: 150,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'MILLIMETERS',
    imperialVal: 8,
    metricVal: 200,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'MILLIMETERS',
    imperialVal: 12,
    metricVal: 300,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'CENTIMETERS',
    imperialVal: 3,
    metricVal: 7.6,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'CENTIMETERS',
    imperialVal: 4,
    metricVal: 10,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'CENTIMETERS',
    imperialVal: 6,
    metricVal: 15,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'CENTIMETERS',
    imperialVal: 8,
    metricVal: 20,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'CENTIMETERS',
    imperialVal: 12,
    metricVal: 30,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'MICRONS',
    imperialVal: 3,
    metricVal: 76000,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'MICRONS',
    imperialVal: 4,
    metricVal: 100000,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'MICRONS',
    imperialVal: 6,
    metricVal: 150000,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'MICRONS',
    imperialVal: 8,
    metricVal: 200000,
  },
  {
    imperialUnit: 'INCHES',
    metricUnit: 'MICRONS',
    imperialVal: 12,
    metricVal: 300000,
  },
];

// base is mm
const BASE_UNIT_TO_MICRON = 1000;
const BASE_UNIT_TO_CM = 0.1;
const BASE_UNIT_TO_INCH = (1 / 25.4);

const MM_TO_INCH = (1 / 25.4);
const CM_TO_INCH = (1 / 2.54);
const MICRON_TO_INCH = (1 / 25400);
const MILS_TO_INCH = 0.001;

export class UtilityFunctions {
  public static getDieTypeIdFromName = (name: string, dieTypes: DieType[]) => {
    const filteredDieTypes = dieTypes.filter((dt: DieType) => dt.name === name);
    if (filteredDieTypes.length > 0) {
      return filteredDieTypes[0].id;
    }
    return null;
  };

  public static getDieTypeFromId = (id: string, dieTypes: DieType[]) => {
    const filteredDieTypes = dieTypes.filter((dt: DieType) => dt.id === id);
    if (filteredDieTypes.length > 0) {
      return filteredDieTypes[0];
    }
    return null;
  };

  public static checkFilter = (value: string, filterValue: string, filterType: string) => {
    switch (filterType) {
      case 'contains': return value.includes(filterValue);
      case 'notcontains': return !value.includes(filterValue);
      case 'startswith': return value.startsWith(filterValue);
      case 'endswith': return value.endsWith(filterValue);
      case '=': return value === filterValue;
      case '<>': return value !== filterValue;
      default: return value.includes(filterValue);
    }
  };

  public static capitalizeFirstLetter = (str: string) => {
    if (!str) return '';
    const separateWord = str.toLowerCase().split(' ');
    for (let i = 0; i < separateWord.length; i += 1) {
      separateWord[i] = separateWord[i].charAt(0).toUpperCase()
        + separateWord[i].substring(1);
    }
    return separateWord.join(' ');
  };

  public static isDataGridInEditMode = (dataGridRef: DataGrid | null) => {
    if (dataGridRef) {
      const rows = dataGridRef.instance.getVisibleRows();
      const notInEditMode = rows.reduce((prevVal: boolean, row: any) => {
        return prevVal && !row.isEditing;
      }, true);
      return !notInEditMode;
    }
    return false;
  };

  public static getReticleGridRectCoordOfDie = (dieData: WaferMapData, reticleSize: { x: number, y: number }) => {
    // returns the grid coords of reticle in which the curr die falls in
    return {
      startX: (dieData.x as number) - (dieData.reticleX as number),
      startY: (dieData.y as number) - (dieData.reticleY as number),
      endX: (dieData.x as number) - (dieData.reticleX as number) + reticleSize.x - 1,
      endY: (dieData.y as number) - (dieData.reticleY as number) + reticleSize.y - 1,
    };
  };

  public static deleteReticleInfo = (dieData: WaferMapData, dieTypes: DieType[]) => {
    // eslint-disable-next-line no-param-reassign
    delete dieData.reticleX;
    // eslint-disable-next-line no-param-reassign
    delete dieData.reticleY;
    // eslint-disable-next-line no-param-reassign
    delete dieData.referenceReticle;
    // eslint-disable-next-line no-param-reassign
    delete dieData.reticleSite;

    const watPcmDieTypeId = UtilityFunctions.getDieTypeIdFromName('WATPCM', dieTypes);
    if (watPcmDieTypeId !== null && dieData.dieType === watPcmDieTypeId) {
      // eslint-disable-next-line no-param-reassign
      dieData.dieType = UtilityFunctions.getDieTypeIdFromName('UnAssigned', dieTypes) || '';
    }
  };

  public static getYCoordsOfIntersectionOfVerticleLineWithCircle = (u: number, h: number, k: number, r: number) => {
    // eqn of line: x = u
    // eqn of circle (x - h) ^ 2 + (y - k) ^ 2 = r ^ 2
    const determinant = 4 * k * k - 4 * (k * k + (u - h) * (u - h) - r * r);
    if (determinant < 0) {
      return null; // no point of intersection
    }
    const p1 = (2 * k + Math.sqrt(determinant)) / 2;
    const p2 = (2 * k - Math.sqrt(determinant)) / 2;
    if (p1 === p2) return null; // consider tangency as no intersection
    return { p1, p2 };
  };

  public static getXCoordsOfIntersectionOfHorizontalLineWithCircle = (u: number, h: number, k: number, r: number) => {
    // eqn of line: y = u
    // eqn of circle (x - h) ^ 2 + (y - k) ^ 2 = r ^ 2
    const determinant = 4 * h * h - 4 * (h * h + (u - k) * (u - k) - r * r);
    if (determinant < 0) {
      return null; // no point of intersection
    }
    const p1 = (2 * h + Math.sqrt(determinant)) / 2;
    const p2 = (2 * h - Math.sqrt(determinant)) / 2;
    if (p1 === p2) return null; // consider tangency as no intersection
    return { p1, p2 };
  };

  public static isWaferFlat = (waferDiameter: number) => {
    return waferDiameter < NOTCH_FLAT_TOGGLE_DIAMETER_VALUE;
  };

  public static getDieXOrY = (
    isAxisUpright: boolean,
    axisStart: number,
    axisIncrement: number,
    isAxisDefaultDirection: boolean,
    rowOrColNumber: number,
    offset: number,
    numOfRowsOrCol: number,
  ) => {
    // isAxisDefaultDirection => true if top to bottom or left to right
    if (isAxisUpright) {
      return (axisStart + axisIncrement * rowOrColNumber) + offset;
      // eslint-disable-next-line no-else-return
    } else {
      // eslint-disable-next-line no-lonely-if
      if (isAxisDefaultDirection) {
        return ((numOfRowsOrCol - 1) - rowOrColNumber) + offset;
        // eslint-disable-next-line no-else-return
      } else {
        return rowOrColNumber + offset;
      }
    }
  };

  public static getColAndRowAxisDirection = (direction: WaferAxisDirection) => {
    const downRightCase = {
      colAxisDirection: ColAxisDirection.LeftToRight,
      rowAxisDirection: RowAxisDirection.TopToBottom,
    };
    switch (direction) {
      case WaferAxisDirection.DOWN_LEFT:
        return {
          colAxisDirection: ColAxisDirection.RightToLeft,
          rowAxisDirection: RowAxisDirection.TopToBottom,
        };
      case WaferAxisDirection.DOWN_RIGHT:
        return downRightCase;
      case WaferAxisDirection.UP_LEFT:
        return {
          colAxisDirection: ColAxisDirection.RightToLeft,
          rowAxisDirection: RowAxisDirection.BottomToTop,
        };
      case WaferAxisDirection.UP_RIGHT:
        return {
          colAxisDirection: ColAxisDirection.LeftToRight,
          rowAxisDirection: RowAxisDirection.BottomToTop,
        };
      default:
        return downRightCase;
    }
  };

  public static getWaferAxisDirection = (colAxisDirection: ColAxisDirection, rowAxisDirection: RowAxisDirection) => {
    if (colAxisDirection === ColAxisDirection.LeftToRight && rowAxisDirection === RowAxisDirection.TopToBottom) {
      return WaferAxisDirection.DOWN_RIGHT;
    } else if (colAxisDirection === ColAxisDirection.LeftToRight && rowAxisDirection === RowAxisDirection.BottomToTop) {
      return WaferAxisDirection.UP_RIGHT;
    } else if (colAxisDirection === ColAxisDirection.RightToLeft && rowAxisDirection === RowAxisDirection.TopToBottom) {
      return WaferAxisDirection.DOWN_LEFT;
    }
    return WaferAxisDirection.UP_LEFT;
  };

  public static getDieTrueXOrY = (xOrYCalc: number, isAxisDefaultDirection: boolean, waferMaxRowsOrCols: number, rowOrColOffset: number) => {
    // isAxisDefaultDirection => true if top to bottom or left to right
    if (isAxisDefaultDirection) {
      return xOrYCalc;
    } else {
      return waferMaxRowsOrCols - 1 - xOrYCalc + 2 * rowOrColOffset;
    }
  };

  private static micronToBaseUnit = (value: number) => {
    return value / BASE_UNIT_TO_MICRON;
  };

  private static cmToBaseUnit = (value: number) => {
    return value / BASE_UNIT_TO_CM;
  };

  private static dieToBaseUnit = (value: number, dieSide?: 'x' | 'y', dieDimensions?: { dieSizeX: number, dieSizeY: number, dieUOM: string }) => {
    if (dieDimensions && dieSide) {
      if (dieSide === 'x') {
        return UtilityFunctions.toBaseUnit({ value: dieDimensions.dieSizeX, UOM: dieDimensions.dieUOM }) * value;
      } else if (dieSide === 'y') {
        return UtilityFunctions.toBaseUnit({ value: dieDimensions.dieSizeY, UOM: dieDimensions.dieUOM }) * value;
      } else {
        return value;
      }
    } else {
      return value;
    }
  };

  public static inchToOtherUnit = (value: number, UOM: string, isDiameter?: boolean) => {
    let conversionFactor = 1;
    if (UOM === 'CENTIMETERS') {
      conversionFactor = CM_TO_INCH;
    } else if (UOM === 'MICRONS') {
      conversionFactor = MICRON_TO_INCH;
    } else if (UOM === 'MILLIMETERS') {
      conversionFactor = MM_TO_INCH;
    } else if (UOM === 'MILS') {
      conversionFactor = MILS_TO_INCH;
    }
    if (isDiameter) {
      const mapping = IMPERIAL_TO_METRIC_CONVERSION_MAPPINGS.find((m) => {
        return (m.imperialUnit === 'INCHES' && m.imperialVal === value && m.metricUnit === UOM);
      });
      if (mapping) {
        return mapping.metricVal;
      } else {
        return value / conversionFactor;
      }
    } else {
      return value / conversionFactor;
    }
  };

  private static inchToBaseUnit = (value: number, isDiameter?: boolean) => {
    if (isDiameter) {
      const mapping = IMPERIAL_TO_METRIC_CONVERSION_MAPPINGS.find((m) => {
        return (m.imperialUnit === 'INCHES' && m.imperialVal === value && m.metricUnit === 'MILLIMETERS');
      });
      if (mapping) {
        return mapping.metricVal;
      } else {
        return value / BASE_UNIT_TO_INCH;
      }
    } else {
      return value / BASE_UNIT_TO_INCH;
    }
  };

  public static toBaseUnit = (
    data: {
      value: number,
      UOM: string,
      dieSide?: 'x' | 'y',
      dieDimensions?: { dieSizeX: number, dieSizeY: number, dieUOM: string },
      isDiameter?: boolean,
    },
  ): number => {
    switch (data.UOM) {
      case 'MICRONS':
        return UtilityFunctions.micronToBaseUnit(data.value);
      case 'MILLIMETERS':
        return data.value;
      case 'CENTIMETERS':
        return UtilityFunctions.cmToBaseUnit(data.value);
      case 'MILS':
        return data.value * 0.0254;
      case 'die':
        return UtilityFunctions.dieToBaseUnit(data.value, data.dieSide, data.dieDimensions);
      case 'INCHES':
        return UtilityFunctions.inchToBaseUnit(data.value, data.isDiameter);
      default:
        return data.value;
    }
  };

  public static euclideanDistance(p1: XYPoint, p2: XYPoint) {
    const a = p1.x - p2.x;
    const b = p1.y - p2.y;
    return Math.sqrt(a * a + b * b);
  }

  public static coordInRange(coord: XYPoint, xLimit: { max: number, min: number }, yLimit: { max: number, min: number }) {
    return coord.x >= xLimit.min && coord.x <= xLimit.max && coord.y >= yLimit.min && coord.y <= yLimit.max;
  }

  public static getReticleReference(x: number, y: number, retWidth: number, retHeight: number, reference: ReticleReference) {
    switch (reference) {
      case 'CENTER': return { x: (x + 0.5) * retWidth, y: (y + 0.5) * retHeight };
      case 'TOP_LEFT': return { x: x * retWidth, y: y * retHeight };
      case 'TOP_RIGHT': return { x: (x + 1) * retWidth, y: y * retHeight };
      case 'BOTTOM_LEFT': return { x: x * retWidth, y: (y + 1) * retHeight };
      case 'BOTTOM_RIGHT': return { x: (x + 1) * retWidth, y: (y + 1) * retHeight };
      default: return { x: (x + 0.5) * retWidth, y: (y + 0.5) * retHeight };
    }
  }

  public static getReticleTopLeft = (
    waferRows: number,
    waferCols: number,
    reticleReference: ReticleReference,
    reticleOffset: { x: number; y: number },
    reticleSize: { x: number; y: number },
    waferBGOffsetXDies: number,
    waferBGOffsetYDies: number,
  ) => {
    // reticle offset in terms of die step size units
    const waferCenter = { x: waferCols / 2, y: waferRows / 2 };
    const waferBGCenter = {
      x: waferCenter.x + waferBGOffsetXDies,
      y: waferCenter.y + waferBGOffsetYDies,
    };
    const reticleReferencePoint = {
      x: waferBGCenter.x + reticleOffset.x,
      y: waferBGCenter.y - reticleOffset.y,
    };

    let reticleTopLeft: { x: number; y: number };
    switch (reticleReference) {
      case 'BOTTOM_LEFT':
        reticleTopLeft = {
          x: reticleReferencePoint.x,
          y: reticleReferencePoint.y - reticleSize.y,
        };
        break;
      case 'BOTTOM_RIGHT':
        reticleTopLeft = {
          x: reticleReferencePoint.x - reticleSize.x,
          y: reticleReferencePoint.y - reticleSize.y,
        };
        break;
      case 'TOP_LEFT':
        reticleTopLeft = {
          x: reticleReferencePoint.x,
          y: reticleReferencePoint.y,
        };
        break;
      case 'TOP_RIGHT':
        reticleTopLeft = {
          x: reticleReferencePoint.x - reticleSize.x,
          y: reticleReferencePoint.y,
        };
        break;
      case 'CENTER':
      default:
        reticleTopLeft = {
          x: reticleReferencePoint.x - reticleSize.x / 2,
          y: reticleReferencePoint.y - reticleSize.y / 2,
        };
        break;
    }

    return {
      x: UtilityFunctions.ignoreDecimalsIfVerySmall(reticleTopLeft.x),
      y: UtilityFunctions.ignoreDecimalsIfVerySmall(reticleTopLeft.y),
    };
  };

  public static buildReticleReapplyAfterGridEdit = (data: {
    waferDefinitionData: any;
    waferMapConfig: any;
  }): null | { reticleTopLeft: { x: number; y: number }; changedVariables: any } => {
    const { waferDefinitionData, waferMapConfig } = data;
    if (!waferMapConfig) return null;
    const waferRows = waferMapConfig?.data?.dies?.length ?? 0;
    const waferCols = waferRows > 0 && Array.isArray(waferMapConfig.data.dies[0])
      ? waferMapConfig.data.dies[0].length
      : 0;
    if (waferRows === 0 || waferCols === 0) return null;

    const reticleSize = {
      x:
        waferDefinitionData?.standardReticle?.reticle?.length > 0
          ? waferDefinitionData.standardReticle.reticle[0].length
          : 0,
      y: waferDefinitionData?.standardReticle?.reticle?.length ?? 0,
    };

    const reticleOffset = {
      x:
        UtilityFunctions.toBaseUnit({
          value: waferDefinitionData.reticleOffsetX,
          UOM: waferDefinitionData.reticleUOM,
        }) /
        UtilityFunctions.toBaseUnit({
          value: waferDefinitionData.dieStepSizeX,
          UOM: waferDefinitionData.dieUOM,
        }),
      y:
        UtilityFunctions.toBaseUnit({
          value: waferDefinitionData.reticleOffsetY,
          UOM: waferDefinitionData.reticleUOM,
        }) /
        UtilityFunctions.toBaseUnit({
          value: waferDefinitionData.dieStepSizeY,
          UOM: waferDefinitionData.dieUOM,
        }),
    };

    const rawReticleTopLeft = UtilityFunctions.getReticleTopLeft(
      waferRows,
      waferCols,
      waferDefinitionData.reticleReference,
      reticleOffset,
      reticleSize,
      waferMapConfig.waferBGOffsetXDies,
      waferMapConfig.waferBGOffsetYDies,
    );
    // Grid edits (row/col insert) adjust BG offsets by 0.5 die steps, which can yield
    // fractional top-left values. Snap to integers so reticle re-apply is not skipped.
    const reticleTopLeft = {
      x: Math.round(rawReticleTopLeft.x),
      y: Math.round(rawReticleTopLeft.y),
    };

    const plotSequence = waferDefinitionData.reticlePlotSequence || 'HORIZONTAL_LEFT_TO_RIGHT';
    const reticleNumberStartX = waferDefinitionData.reticleNumberStartX !== undefined
      && waferDefinitionData.reticleNumberStartX !== null
      && waferDefinitionData.reticleNumberStartX !== ''
      ? Number(waferDefinitionData.reticleNumberStartX)
      : 0;
    const reticleNumberStartY = waferDefinitionData.reticleNumberStartY !== undefined
      && waferDefinitionData.reticleNumberStartY !== null
      && waferDefinitionData.reticleNumberStartY !== ''
      ? Number(waferDefinitionData.reticleNumberStartY)
      : 0;

    const dieTextField = waferMapConfig?.dieTextField || 'reticleSite';

    const changedVariables = {
      showDieText: true,
      dieTextField,
      reticleSize,
      overlayReticle: true,
      reticleReference: waferDefinitionData.reticleReference,
      standardReticle: waferDefinitionData.standardReticle,
      reticlePlotSequence: plotSequence,
      reticleNumberStartX,
      reticleNumberStartY,
      reticleOriginStartX: waferDefinitionData.reticleOriginStartX,
      reticleOriginStartY: waferDefinitionData.reticleOriginStartY,
    };

    return { reticleTopLeft, changedVariables };
  };

  public static areFloatsEqual(a: number, b: number) {
    return Math.abs(a - b) < 0.0000001;
  }

  public static getMaxRowsAndCols = (data: Dies) => {
    let maxCols = 0;
    for (let i = 0; i < data.length; i += 1) {
      if (maxCols < data[i].length) {
        maxCols = data[i].length;
      }
    }
    return {
      maxRows: data.length,
      maxCols,
    };
  };

  public static getPcmWatMaxReticleRowsCols = (options: {
    waferDefinitionData: {
      reticleSizeX: number;
      reticleSizeY: number;
      dieStepSizeX: number;
      dieStepSizeY: number;
      dieUOM: string;
      waferDiameter: number;
      waferDiameterUOM: string;
    };
    waferMapConfig?: {
      data?: { dies?: Dies };
      reticleGridRectCoords?: { [key: string]: unknown };
    } | null;
  }): { maxReticleRows: number; maxReticleCols: number } => {
    const { waferDefinitionData, waferMapConfig } = options;
    const reticleSizeX = +waferDefinitionData.reticleSizeX;
    const reticleSizeY = +waferDefinitionData.reticleSizeY;
    const dies = waferMapConfig?.data?.dies ?? [];
    const reticleGridRectCoords = waferMapConfig?.reticleGridRectCoords;

    if (reticleSizeX > 0 && reticleSizeY > 0) {
      const rowIndices = new Set<number>();
      const colIndices = new Set<number>();

      if (reticleGridRectCoords && Object.keys(reticleGridRectCoords).length > 0) {
        Object.keys(reticleGridRectCoords).forEach((key) => {
          try {
            const rect = JSON.parse(key) as { startX: number; startY: number };
            rowIndices.add(rect.startY / reticleSizeY);
            colIndices.add(rect.startX / reticleSizeX);
          } catch {
            // ignore malformed key and continue with die-based inference
          }
        });
      } else if (dies.length > 0) {
        const reticleSize = { x: reticleSizeX, y: reticleSizeY };
        for (let i = 0; i < dies.length; i += 1) {
          for (let j = 0; j < dies[i].length; j += 1) {
            const dieData = dies[i][j];
            if (
              dieData
              && !dieData.isCropped
              && !dieData.isDeleted
              && dieData.reticleX !== undefined
              && dieData.reticleY !== undefined
            ) {
              const rect = UtilityFunctions.getReticleGridRectCoordOfDie(dieData, reticleSize);
              rowIndices.add(rect.startY / reticleSizeY);
              colIndices.add(rect.startX / reticleSizeX);
            }
          }
        }
      }

      if (rowIndices.size > 0 && colIndices.size > 0) {
        return { maxReticleRows: rowIndices.size, maxReticleCols: colIndices.size };
      }
    }

    const dieStepSizeX = UtilityFunctions.toBaseUnit({
      value: waferDefinitionData.dieStepSizeX,
      UOM: waferDefinitionData.dieUOM,
    });
    const dieStepSizeY = UtilityFunctions.toBaseUnit({
      value: waferDefinitionData.dieStepSizeY,
      UOM: waferDefinitionData.dieUOM,
    });
    const waferDiameter = UtilityFunctions.toBaseUnit({
      value: waferDefinitionData.waferDiameter,
      UOM: waferDefinitionData.waferDiameterUOM,
      isDiameter: true,
    });
    const reticleWidth = dieStepSizeX * reticleSizeX;
    const reticleHeight = dieStepSizeY * reticleSizeY;
    if (!reticleWidth || !reticleHeight) {
      return { maxReticleRows: 0, maxReticleCols: 0 };
    }
    return {
      maxReticleRows: Math.ceil(waferDiameter / reticleHeight) + 1,
      maxReticleCols: Math.ceil(waferDiameter / reticleWidth) + 1,
    };
  };

  public static markDieUnderSelection = (
    arrayIndices: RectCoord,
    value: boolean,
    startRow: number,
    startCol: number,
    rowDirection: number,
    colDirection: number,
    waferData: any,
    enabledDieTypes?: { [key: string]: boolean },
  ) => {
    for (let i = arrayIndices.startY; i <= arrayIndices.endY; i += 1) {
      for (let j = arrayIndices.startX; j <= arrayIndices.endX; j += 1) {
        const rIndex = startRow + rowDirection * i;
        const cIndex = startCol + colDirection * j;
        if (waferData.dies[rIndex] && waferData.dies[rIndex][cIndex]) {
          const die = waferData.dies[rIndex][cIndex];
          if (value && enabledDieTypes && die && die.dieType && enabledDieTypes[die.dieType] === false) {
            // skip setting under-selection for disabled die types
            continue;
          }
          // eslint-disable-next-line no-param-reassign
          waferData.dies[rIndex][cIndex]!.isUnderSelection = value;
        }
      }
    }
  };

  public static createTemporaryDiesForSelection = (
    arrayIndices: RectCoord,
    startRow: number,
    startCol: number,
    rowDirection: number,
    colDirection: number,
    waferData: any,
  ) => {
    for (let i = arrayIndices.startY; i <= arrayIndices.endY; i += 1) {
      for (let j = arrayIndices.startX; j <= arrayIndices.endX; j += 1) {
        const rIndex = startRow + rowDirection * i;
        const cIndex = startCol + colDirection * j;

        // Only operate within current grid bounds - do not expand the array
        if (
          rIndex < 0 || cIndex < 0
          || rIndex >= waferData.dies.length
          || !waferData.dies[rIndex]
          || cIndex >= waferData.dies[rIndex].length
        ) {
          continue;
        }

        const currentDie = waferData.dies[rIndex][cIndex];
        // Do not affect existing non-cropped dies; empty slots and cropped get temp die for selection
        const isEmptySlot = currentDie === null || currentDie === undefined;
        const isCropped = currentDie?.isCropped === true;
        if (isEmptySlot) {
          // eslint-disable-next-line no-param-reassign
          waferData.dies[rIndex][cIndex] = {
            binColor: '#CCCCCC',
            isSelected: false,
            isCropped: false,
            isDeleted: false,
            isUnderSelection: true,
            isTemporary: true,
            dieType: null,
            x: 0,
            y: 0,
          };
        } else if (isCropped) {
          // Cropped areas: replace with same temp die so selection preview shows; restore on remove
          // eslint-disable-next-line no-param-reassign
          waferData.dies[rIndex][cIndex] = {
            binColor: '#CCCCCC',
            isSelected: false,
            isCropped: false,
            isDeleted: false,
            isUnderSelection: true,
            isTemporary: true,
            isReplacedCropped: true,
            dieType: null,
            x: 0,
            y: 0,
          };
        }
      }
    }
  };

  public static removeTemporaryDiesForSelection = (
    arrayIndices: RectCoord,
    startRow: number,
    startCol: number,
    rowDirection: number,
    colDirection: number,
    waferData: any,
  ) => {
    for (let i = arrayIndices.startY; i <= arrayIndices.endY; i += 1) {
      for (let j = arrayIndices.startX; j <= arrayIndices.endX; j += 1) {
        const rIndex = startRow + rowDirection * i;
        const cIndex = startCol + colDirection * j;
        if (waferData.dies[rIndex] && waferData.dies[rIndex][cIndex]) {
          const dieData = waferData.dies[rIndex][cIndex];
          if (dieData && dieData.isTemporary) {
            if (dieData.isReplacedCropped) {
              // Restore cropped die so insert can still target it later
              // eslint-disable-next-line no-param-reassign
              waferData.dies[rIndex][cIndex] = {
                binColor: '#CCCCCC',
                isSelected: false,
                isCropped: true,
                isDeleted: false,
                dieType: null,
                x: 0,
                y: 0,
              };
            } else {
              // eslint-disable-next-line no-param-reassign
              waferData.dies[rIndex][cIndex] = null;
            }
          } else if (dieData && dieData.isUnderSelection) {
            // eslint-disable-next-line no-param-reassign
            dieData.isUnderSelection = false;
          }
        }
      }
    }
  };

  public static getTranslatedCoords = (rect: RectCoord, viewport: Viewport, canvasHeight: number) => {
    // From
    // startX, endX => [0 - canvas.width]+
    // startY, endY => [0 - canvas.height]+

    // To
    // startX, endX => [0 - viewport.width]+
    // startY, endY => [0 - viewport.height]+

    return {
      startX: rect.startX - viewport.x,
      endX: rect.endX - viewport.x,
      startY: viewport.height + (viewport.y - canvasHeight + rect.startY),
      endY: viewport.height + (viewport.y - canvasHeight + rect.endY),
    };
  };

  public static rotate = (point: XYPoint, angle: number, isCLockwise: boolean) => {
    return isCLockwise ? {
      x: point.x * Math.cos(angle) + point.y * Math.sin(angle),
      y: point.y * Math.cos(angle) - point.x * Math.sin(angle),
    } : {
      x: point.x * Math.cos(angle) - point.y * Math.sin(angle),
      y: point.y * Math.cos(angle) + point.x * Math.sin(angle),
    };
  };

  public static solveSimultaneous = (a: number, b: number, c: number, p: number, q: number, r: number) => {
    if (((a * q - p * b) !== 0) && ((b * p - q * a) !== 0)) {
      // In this case we have a unique solution and display x and y
      return {
        x: (c * q - r * b) / (a * q - p * b),
        y: (c * p - r * a) / (b * p - q * a),
      };
    }
    return null;
  };

  private static componentToHex = (c: number) => {
    const a = c;
    const hex = a.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };

  public static rgbToHex = (r: number, g: number, b: number) => {
    return `#${UtilityFunctions.componentToHex(r)}${UtilityFunctions.componentToHex(g)}${UtilityFunctions.componentToHex(b)}`;
  };

  public static ignoreDecimalsIfVerySmall = (x: number) => {
    return Math.abs((x) - Math.round(x)) < 0.000000001 ? Math.round(x) : x;
  };

  public static toNotchPositionEnum = (notchPosition: string) => {
    switch (notchPosition) {
      case 'U':
        return NotchPosition.UP;
      case 'D':
        return NotchPosition.DOWN;
      case 'L':
        return NotchPosition.LEFT;
      case 'R':
        return NotchPosition.RIGHT;
      default:
        return DefaultNotchPosition;
    }
  };

  public static toRotationAngle = (notchPosition: string) => {
    switch (notchPosition) {
      case 'U':
        return 180;
      case 'D':
        return 0;
      case 'L':
        return 90;
      case 'R':
        return 270;
      default:
        return 0;
    }
  };

  public static resolveWcmDisplayNotchPosition = (
    outputNotchPosition: string,
    inputNotchPosition: string,
  ): NotchPosition => {
    const resolvedNotch = outputNotchPosition && outputNotchPosition !== 'UNKNOWN'
      ? outputNotchPosition
      : inputNotchPosition;
    switch (resolvedNotch) {
      case 'UP':
        return NotchPosition.UP;
      case 'DOWN':
        return NotchPosition.DOWN;
      case 'LEFT':
        return NotchPosition.LEFT;
      case 'RIGHT':
        return NotchPosition.RIGHT;
      default:
        return DefaultNotchPosition;
    }
  };

  public static fromCamelToNormalCase = (str: string) => {
    return str.replace(/([A-Z])/g, ' $1').replace(/^./, (str_: string) => { return str_.toUpperCase(); });
  }

  public static upperToLowerCase(str: string) {
    return str.toLowerCase().replace(/_/g, ' ');
  }
}
